import React, { useState } from "react";
import { Box, Drawer, SxProps, Theme } from "@mui/material";
import { JsonSchemaRoot } from "./types";
import { SchemaHierarchyTree } from "./SchemaHierarchyTree";
import { SchemaDetailEditor } from "./SchemaDetailEditor";
import { sxClsx } from "utils/styleClsx";
import { SchemaPath } from "./constants";

interface JsonSchemaEditorProps {
    value: JsonSchemaRoot;
    onChange: (schema: JsonSchemaRoot) => void;
    sx?: SxProps<Theme>;
}

/**
 * JsonSchemaEditor - two-pane interface for JSON Schema editing
 * Left pane: Hierarchy tree of schema structure
 * Right pane: Detailed editor for selected schema node
 */
const JsonSchemaEditorControlled: React.FC<JsonSchemaEditorProps> = ({
    value,
    onChange,
    ...props
}) => {
    const [selectedPath, setSelectedPath] = useState<SchemaPath>([]);
    const [addPropertyPath, setAddPropertyPath] = useState<SchemaPath | null>(
        null,
    );
    const [addItemPath, setAddItemPath] = useState<SchemaPath | null>(null);

    const isPathLocked = (path: SchemaPath): boolean => {
        return false;
    };

    const handleSchemaUpdate = (updatedSchema: JsonSchemaRoot) => {
        onChange?.(updatedSchema);
    };

    return (
        // The two panes below need 1200px to stay usable, which is wider than
        // some containers. Scroll that width inside this wrapper rather than
        // letting the panes overflow and paint outside their container.
        <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
            <Box
                sx={sxClsx(
                    {
                        display: "flex",
                        height: "600px",
                        overflow: "hidden",
                        minWidth: "1200px",
                    },
                    props.sx,
                )}
            >
                {/* Left Pane - Schema Hierarchy */}
                <Drawer
                    variant="permanent"
                    sx={{
                        width: 300,
                        flexShrink: 0,
                        zIndex: 10,
                        "& .MuiDrawer-paper": {
                            width: 300,
                            maxHeight: "100%",
                            boxSizing: "border-box",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            overflow: "auto",
                            position: "relative",
                        },
                    }}
                >
                    <SchemaHierarchyTree
                        schema={value}
                        selectedPath={selectedPath}
                        onSelectPath={setSelectedPath}
                        onAddProperty={setAddPropertyPath}
                        onAddItem={setAddItemPath}
                    />
                </Drawer>

                {/* Right Pane - Detail Editor */}
                <Box
                    sx={{
                        flexGrow: 1,
                        width: 0,
                        maxHeight: "100%",
                        overflow: "auto",
                        padding: 3,
                    }}
                >
                    <SchemaDetailEditor
                        schema={value}
                        selectedPath={selectedPath}
                        onSchemaUpdate={handleSchemaUpdate}
                        addPropertyPath={addPropertyPath}
                        onAddPropertyPathChange={setAddPropertyPath}
                        addItemPath={addItemPath}
                        onAddItemPathChange={setAddItemPath}
                        isPathLocked={isPathLocked}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default JsonSchemaEditorControlled;
